import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { vendorPaymentsService } from '../../services/payables/vendor-payments';
import { vendorsService } from '../../services/payables/vendors';
import { useAuth } from '../../contexts/AuthContext';

const PayBillModal = ({ bill, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    description: '',
    reference: ''
  });
  const [errors, setErrors] = useState({});
  const [existingPayments, setExistingPayments] = useState([]);

  useEffect(() => {
    if (isOpen && bill) {
      loadData();
      resetForm();
    }
  }, [isOpen, bill]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      // Load vendor details
      if (bill.vendorId) {
        const vendorData = await vendorsService.getVendor(bill.vendorId);
        setVendor(vendorData);
      }

      // Load existing payments for this bill
      const payments = await vendorPaymentsService.getVendorPayments(user.companyId);
      const billPayments = payments.filter(payment => payment.billId === bill.id);
      setExistingPayments(billPayments);

      // Set default payment amount to remaining balance (but keep empty if 0)
      const totalPaid = billPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const remainingAmount = (bill.totalAmount || 0) - totalPaid;
      
      setFormData(prev => ({
        ...prev,
        amount: remainingAmount > 0 ? remainingAmount : '',
        description: `Payment for Bill ${bill.billNumber}`
      }));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash',
      description: '',
      reference: ''
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? value : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getTotalPaid = () => {
    return existingPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  };

  const getRemainingAmount = () => {
    return (bill.totalAmount || 0) - getTotalPaid();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = t('bill_payments.validAmount') + ' ' + t('bill_payments.required');
    }
    
    if (!isNaN(amount) && amount > getRemainingAmount()) {
      newErrors.amount = t('bill_payments.paymentExceedsBalance');
    }
    
    if (!formData.paymentDate) {
      newErrors.paymentDate = t('bill_payments.paymentDate') + ' ' + t('bill_payments.required');
    }
    
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = t('bill_payments.paymentMethod') + ' ' + t('bill_payments.required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Pre-flight validation
    if (!user?.companyId) {
      setErrors({ submit: 'Company ID is missing. Please refresh and try again.' });
      return;
    }
    
    if (!user?.uid) {
      setErrors({ submit: 'User authentication is missing. Please login again.' });
      return;
    }
    
    if (!bill?.id) {
      setErrors({ submit: 'Bill ID is missing. Please refresh and try again.' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      console.log('🔄 PayBillModal: Starting payment submission');
      console.log('💰 Payment amount:', formData.amount);
      console.log('🏢 Company ID:', user.companyId);
      console.log('📄 Bill ID:', bill.id);
      console.log('👤 User ID:', user.uid);
      console.log('🏪 Vendor ID:', bill.vendorId);
      
      const paymentData = {
        companyId: user.companyId,
        vendorId: bill.vendorId,
        billId: bill.id,
        amount: parseFloat(formData.amount),
        paymentDate: new Date(formData.paymentDate),
        paymentMethod: formData.paymentMethod,
        description: formData.description || `Payment for Bill ${bill.billNumber}`,
        reference: formData.reference || '',
        vendorName: vendor?.name || vendor?.companyName || bill.vendorName || 'Unknown Vendor',
        billNumber: bill.billNumber,
        status: 'completed',
        createdBy: user.uid
      };
      
      console.log('📤 Payment data to submit:', paymentData);
      
      const result = await vendorPaymentsService.addVendorPayment(paymentData);
      console.log('✅ Payment created successfully with ID:', result);
      console.log('📊 Payment data saved:', paymentData);
      
      // Trigger refresh of parent components
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('❌ Error processing payment:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      
      // Show more specific error message
      let errorMessage = t('bill_payments.errorProcessingPayment');
      if (error.message) {
        if (error.message.includes('permission')) {
          errorMessage = 'Permission denied. Please check your access rights.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Please check your internet connection.';
        } else {
          errorMessage += ': ' + error.message;
        }
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleQuickAmount = (percentage) => {
    const amount = getRemainingAmount() * (percentage / 100);
    setFormData(prev => ({
      ...prev,
      amount: amount
    }));
  };

  if (!isOpen || !bill) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('bill_payments.payBill')}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('bill_payments.bill')} #{bill.billNumber} - {vendor?.name || t('bill_payments.loadingVendor')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Bill Summary */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">{t('bill_payments.billSummary')}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">{t('bill_payments.billDate')}</p>
                <p className="text-sm font-medium">{formatDate(bill.billDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('bill_payments.dueDate')}</p>
                <p className="text-sm font-medium">{formatDate(bill.dueDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('bill_payments.totalAmount')}</p>
                <p className="text-sm font-medium">{formatCurrency(bill.totalAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('bill_payments.totalPaid')}</p>
                <p className="text-sm font-medium text-green-600">{formatCurrency(getTotalPaid())}</p>
              </div>
              <div className="col-span-2 pt-2 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">{t('bill_payments.remainingBalance')}</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(getRemainingAmount())}</p>
                  </div>
                  {existingPayments.length > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{t('bill_payments.lastPayment')}:</p>
                      <p className="text-sm font-medium text-green-600">
                        {formatCurrency(existingPayments[existingPayments.length - 1]?.amount)} 
                        <span className="text-xs text-gray-500 ml-1">
                          {t('bill_payments.on')} {formatDate(existingPayments[existingPayments.length - 1]?.paymentDate)}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.paymentAmount')} *
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.amount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('bill_payments.enterAmount')}
                  step="0.01"
                  min="0"
                  max={getRemainingAmount()}
                />
                <div className="absolute right-3 top-2 text-sm text-gray-500">
                  SAR
                </div>
              </div>
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
              )}
              
              {/* Quick Amount Buttons */}
              <div className="mt-2 flex space-x-2">
                <button
                  type="button"
                  onClick={() => handleQuickAmount(25)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  25%
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickAmount(50)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  50%
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickAmount(75)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  75%
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickAmount(100)}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  {t('bill_payments.fullAmount')}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.paymentDate')} *
              </label>
              <input
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.paymentDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.paymentDate && (
                <p className="mt-1 text-sm text-red-600">{errors.paymentDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.paymentMethod')} *
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.paymentMethod ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="cash">{t('bill_payments.cash')}</option>
                <option value="check">{t('bill_payments.check')}</option>
                <option value="bank_transfer">{t('bill_payments.bankTransfer')}</option>
                <option value="credit_card">{t('bill_payments.creditCard')}</option>
                <option value="digital_wallet">{t('bill_payments.digitalWallet')}</option>
              </select>
              {errors.paymentMethod && (
                <p className="mt-1 text-sm text-red-600">{errors.paymentMethod}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.reference')}
              </label>
              <input
                type="text"
                name="reference"
                value={formData.reference}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('bill_payments.enterReference')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.description')}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('bill_payments.enterDescription')}
              />
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="text-red-600 text-sm font-medium">{errors.submit}</div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('bill_payments.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || getRemainingAmount() <= 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? t('bill_payments.processing') : t('bill_payments.processPayment')}
              </button>
            </div>
          </form>

          {/* Existing Payments */}
          {existingPayments.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('bill_payments.existingPayments')}</h3>
              <div className="space-y-3">
                {existingPayments.map((payment, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(payment.amount)}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}>
                            {t(`bill_payments.${payment.status}`)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <p>{t('bill_payments.paymentDate')}: {formatDate(payment.paymentDate)}</p>
                          <p>{t('bill_payments.method')}: {t(`bill_payments.${payment.paymentMethod}`)}</p>
                          {payment.description && <p>{payment.description}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayBillModal; 